import imgRank1 from '../assets/rank-1.svg'
import imgRank2 from '../assets/rank-2.svg'
import imgRank3 from '../assets/rank-3.svg'
import imgRank4 from '../assets/rank-4.svg'
import { motion } from 'framer-motion'
import { useSound } from '../components/SoundHandler'
import { useAccount } from 'wagmi'
import { isAddressEqual } from 'viem'
import { ProfileSlot } from '~/blinko/components/ProfileSlot'
import { MetadataSlot, useMetadata } from '../components/MetadataHandler'
import { getLeauge } from '../assets/leagues/leagues'
import { useResolution } from '../hooks/useResolution'
import { icons } from '../assets/icons/icons'
import useSWR from 'swr'
import { atom, useAtom } from 'jotai'
import { ReactNode, useEffect, useState } from 'react'
import { ProfilePicture } from '../components/ProfilePicture'

import { ContainerShape, HexagonShape, InnerShape } from '../components/Shape'
import { formatNumber } from '~/core/utils/formatNumber'

const atomTab = atom<'total' | 'daily' | 'loser-total' | 'loser-daily'>('daily')

const useResetTimer = () => {
  const [timeLeft, setTimeLeft] = useState('')

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date()
      const nextReset = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate() + 1,
        0, 0, 0, 0
      ))
      const diff = nextReset.getTime() - now.getTime()

      const hours = Math.floor(diff / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)

      if (hours < 1) {
        return `${minutes}m ${seconds}s`
      }
      return `${hours}h ${minutes}m`
    }

    setTimeLeft(calculateTimeLeft())
    const interval = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  return timeLeft
}

export const LeaderboardModal = ({ onDismiss }: { onDismiss: () => void }) => {
  const { address } = useAccount()
  const sound = useSound();
  const { leaderboard, mutateLeaderboard } = useMetadata()
  const { isMobile } = useResolution()
  const resetTimer = useResetTimer()

  const [tab, setTab] = useAtom(atomTab)

  useSWR('leaderboard', mutateLeaderboard, {
    refreshInterval: 1000 * 60 * 10,
  })

  const onClose = () => {
    onDismiss()
    sound.play('click');
  }

  const ui = {
    timer: null as ReactNode,
    data: [] as any[],
    myRank: null as any | null,
  }

  switch (tab) {
    case 'total':
      ui.data = leaderboard?.total.leaderboard ?? []
      ui.myRank = leaderboard?.total.myRank ?? null
      break
    case 'daily':
      ui.data = leaderboard?.daily.leaderboard ?? []
      ui.myRank = leaderboard?.daily.myRank ?? null
      break
  }

  const me = ui.myRank || ui.data.find(x => address && isAddressEqual(address, x.address))

  return (
    <div className="fixed z-30 inset-0">
      <motion.div
        className="absolute inset-0 bg-[#0a0523]/40"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
      />

      <motion.div
        className="absolute inset-0"
        initial={{ backdropFilter: 'blur(20px) opacity(0)' }}
        animate={{ backdropFilter: 'blur(20px) opacity(1)' }}
      />

      <div className='flex flex-col gap-4 md:gap-[45px] px-2 md:px-0 pt-6 pb-4 md:py-10 max-w-[1280px] mx-auto h-full'>
        {isMobile ? (
          <div className="relative">
            <motion.div
              className="flex flex-col items-center justify-center gap-1 py-2.5"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <p className="text-[24px] font-blinko text-center text-white">
                Leaderboard
              </p>
            </motion.div>
            
            <button className='absolute top-0 right-2' onClick={onClose}>
              <HexagonShape cornerRadius={6} strokeWidth={1}>
                <div className="flex justify-center items-center p-2">
                  <img src={icons.close} className='w-6 h-6' />
                </div>
              </HexagonShape>
            </button>
          </div>
        ) : (
          <div className="relative">
            <motion.div
              className="flex flex-col items-center justify-center gap-8"
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
            >
              <p className="text-[48px] font-blinko text-center text-white">
                Leaderboard
              </p>
            </motion.div>
            
            <button className='absolute top-0 left-0' onClick={onClose}>
              <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                <div className="px-8 py-3">
                  <p className="text-base font-blinko text-white">
                    Back to Game
                  </p>
                </div>
              </InnerShape>
            </button>
          </div>
        )}

        <div className='flex-1 overflow-hidden'>
          <ContainerShape {...(
              isMobile 
                ? {cornerDiagonal: 20, cornerRadius: 8, strokeWidth: 2 } 
                : {cornerDiagonal: 60, cornerRadius: 25, strokeWidth: 2 })
          }>
            <div className="h-full overflow-auto">
              <div className='pt-6 px-3 md:px-12 md:pt-12'>
                <div className='flex justify-between items-center mb-1'>
                  <div className="relative flex flex-col gap-1">
                    <div className="flex pr-10">
                      <div className='relative'>
                        <TabButton active={tab === 'daily'} onClick={() => setTab('daily')}>Daily</TabButton>
                        {tab === 'daily' && (
                          <div className="absolute bottom-full left-0 right-0 pb-1 flex gap-0.5 items-center text-xs md:text-sm justify-center text-white/50">
                            <svg className="aspect-square w-2 md:w-4" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5">
                              <path d="M7 4.66675V7.00008L8.45833 8.45841" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M3.26908 3.26908L2.73875 2.73875L2.73875 2.73875L3.26908 3.26908ZM2.53036 4.0078L1.78037 4.01157C1.78244 4.42284 2.11532 4.75573 2.52659 4.75779L2.53036 4.0078ZM4.0115 4.76526C4.4257 4.76734 4.76317 4.43324 4.76526 4.01903C4.76734 3.60483 4.43324 3.26736 4.01903 3.26528L4.01527 4.01527L4.0115 4.76526ZM3.27289 2.51913C3.27081 2.10492 2.93334 1.77083 2.51913 1.77291C2.10492 1.77499 1.77083 2.11246 1.77291 2.52667L2.5229 2.5229L3.27289 2.51913ZM2.54179 6.33364C2.59804 5.92326 2.31097 5.54499 1.90059 5.48874C1.49021 5.43249 1.11194 5.71956 1.05569 6.12994L1.79874 6.23179L2.54179 6.33364ZM10.6936 3.30639L11.2239 2.77606C8.87555 0.427677 5.07658 0.400922 2.73875 2.73875L3.26908 3.26908L3.79941 3.79941C5.54143 2.05739 8.3906 2.06405 10.1633 3.83672L10.6936 3.30639ZM3.30639 10.6936L2.77606 11.2239C5.12445 13.5723 8.92342 13.5991 11.2612 11.2612L10.7309 10.7309L10.2006 10.2006C8.45857 11.9426 5.6094 11.936 3.83672 10.1633L3.30639 10.6936ZM10.7309 10.7309L11.2612 11.2612C13.5991 8.92342 13.5723 5.12445 11.2239 2.77606L10.6936 3.30639L10.1633 3.83672C11.936 5.6094 11.9426 8.45857 10.2006 10.2006L10.7309 10.7309ZM3.26908 3.26908L2.73875 2.73875L2.00003 3.47747L2.53036 4.0078L3.06069 4.53813L3.79941 3.79941L3.26908 3.26908ZM2.53036 4.0078L2.52659 4.75779L4.0115 4.76526L4.01527 4.01527L4.01903 3.26528L2.53413 3.25781L2.53036 4.0078ZM2.53036 4.0078L3.28035 4.00403L3.27289 2.51913L2.5229 2.5229L1.77291 2.52667L1.78037 4.01157L2.53036 4.0078ZM1.79874 6.23179L1.05569 6.12994C0.808715 7.93175 1.38397 9.83184 2.77606 11.2239L3.30639 10.6936L3.83672 10.1633C2.7879 9.11446 2.35628 7.68707 2.54179 6.33364L1.79874 6.23179Z" fill="currentColor" />
                            </svg>
                            <span>{resetTimer}</span>
                          </div>
                        )}
                      </div>
                      <TabButton active={tab === 'total'} onClick={() => setTab('total')}>All Time</TabButton>
                    </div>
                  </div>

                  <MetadataSlot>
                    {({ leaderboardMetadata }) => !isMobile && leaderboardMetadata && (
                      <ProfileSlot address={address}>
                        {() => (
                          <div className="flex items-center gap-3">
                            <HexagonShape cornerRadius={6} strokeWidth={1}>
                              <div className='w-10 h-10 p-2'>
                                <img src={leaderboardMetadata.image} />
                              </div>
                            </HexagonShape>
                            <div className="flex flex-col gap-1">
                              <p className="text-left text-[10px] font-blinko text-white/80">
                                Leaderboard place:
                              </p>
                              <div className="flex items-center relative gap-2">
                                <p className="text-base font-blinko text-white">
                                  {leaderboardMetadata.league === 'None' ? '-' : leaderboardMetadata.rank}
                                </p>
                                <div className="rounded" style={{ border: '1px solid #FFF085'}}>
                                  <div className="px-2 py-0.5 bg-[#F0B10080]">
                                    <p className="text-xs font-semibold text-white">
                                      {leaderboardMetadata.title}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </ProfileSlot>
                    )}
                  </MetadataSlot>
                </div>

                {isMobile && ui.timer && (
                  <div className='mb-4'>
                    {ui.timer}
                  </div>
                )}

                {isMobile ? (
                  <div className='flex'>
                    <div className="w-10 flex items-center px-2 py-2">
                      <p className="text-xs font-medium uppercase text-white/70">
                        #
                      </p>
                    </div>
                    <div className="flex-1 flex items-center gap-2.5 px-4 py-2">
                      <p className="text-xs font-medium uppercase text-white/70">
                        Player
                      </p>
                    </div>
                    <div className="flex items-center py-2 pr-2">
                      <p className="text-xs font-medium uppercase text-white/70">
                        Honey
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className='flex border-b border-solid border-white/20 pointer-events-auto'>
                    <div className="flex-1 flex items-center p-4">
                      <p className="text-base font-semibold uppercase text-white/70">
                        Place
                      </p>
                    </div>
                    <div className="flex-1 flex items-center p-4">
                      <p className="text-base font-semibold uppercase text-white/70">
                        Player
                      </p>
                    </div>
                    <div className="flex-1 flex items-center p-4">
                      <p className="text-base font-semibold uppercase text-white/70">
                        League
                      </p>
                    </div>
                    <div className="flex-1 flex items-center p-4">
                      <p className="text-base font-semibold uppercase text-white/70">
                        Honey
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {isMobile ? (
                  <div className='flex flex-col gap-1 px-3 pb-6'>
                    {me && (
                      <>
                        <div className="mb-1" />
                        <LeaderboardMobileRow x={me} rank={me.rank} isMine={true} isSticky={true} />
                        <div className="h-px bg-white/10 my-1 mx-2" />
                      </>
                    )}
                    {ui.data.map((x) => {
                      const isMine = address && isAddressEqual(address, x.address)
                      const rank = x.rank

                      return (
                        <LeaderboardMobileRow key={x.address} x={x} rank={rank} isMine={isMine} />
                      )
                    })}
                  </div>
              ) : (
                  <div className='flex flex-col gap-1 px-12 pb-12'>
                    {me && (
                      <>
                        <div className="mb-2" />
                        <LeaderboardRow 
                          x={me} 
                          rank={me.rank} 
                          isMine={true} 
                          isSticky={true}
                          league={getLeauge(me.rank)} 
                          onClick={() => {
                            if (me.twitterHandle) {
                              window.open(`https://x.com/${me.twitterHandle}`, '_blank');
                            }
                          }} 
                        />
                        <div className="h-px bg-white/10 my-2 mx-4" />
                      </>
                    )}
                    {ui.data.map((x) => {
                      const isMine = address && isAddressEqual(address, x.address)
                      const rank = x.rank
                      const league = getLeauge(rank)

                      const onClick = () => {
                        if (x.twitterHandle) {
                          window.open(`https://x.com/${x.twitterHandle}`, '_blank');
                        }
                      }

                      return (
                        <LeaderboardRow 
                          key={x.address} 
                          x={x} 
                          rank={rank} 
                          isMine={isMine} 
                          league={league} 
                          onClick={onClick} 
                        />
                      )
                    })}
                  </div>
              )}
            </div>
          </ContainerShape>
        </div>
      </div>
    </div>
  )
};

const LeaderboardRow = ({ x, rank, isMine, isSticky, league, onClick }: any) => {
  return (
    <div key={x.address} className='h-[60px]'>
      <motion.div
        className='flex h-full rounded-2xl cursor-pointer'
        whileHover={{
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
        }}
        animate={{
          backgroundColor: isSticky ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
          border: (isMine || isSticky) ? '1px solid rgba(255, 255, 255, 0.2)' : 'none'
        }}
        onClick={onClick}
      >
        <div className="flex-1 flex items-center px-4">
          <div className='flex items-center gap-2'>
            <div className='w-5'>
              <IconRank rank={rank} />
            </div>
            <p className="text-base font-medium text-white">{rank}</p>
          </div>
        </div>
        <div className="flex-1 flex items-center gap-[10px] px-4">
          <div className='w-8 h-8'>
            <ProfilePicture src={x.picture ?? undefined} twitterHandle={x.twitterHandle} />
          </div>
          <p className="text-sm font-medium text-left text-white">
            {x.name}
            {isMine && <span className='text-white/50'> (You)</span>}
          </p>
        </div>
        <div className="flex-1 flex items-center gap-[10px] px-4">
          <img className='w-[28px] aspect-square' src={league.image} />
          <p className="text-sm text-white/80">
            {league.title}
          </p>
        </div>
        <div className="flex-1 flex items-center gap-[10px] px-4">
          <div className="rounded" style={{ border: '1px solid #FFF085'}}>
            <div className="px-2 py-0.5 bg-[#F0B10080]">
              <p className="text-xs font-semibold text-white">
                {formatNumber(x.totalHoney ?? x.dailyHoney)}
              </p>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  )
}

const LeaderboardMobileRow = ({ x, rank, isMine, isSticky }: any) => {
  return (
    <div key={x.address} className='h-[40px]'>
      <div
        className={`flex rounded-lg h-full cursor-pointer ${isSticky ? 'bg-white/10' : 'bg-white/5'} ${isSticky ? 'border border-solid border-white/20' : ''}`}
      >
        <div className="w-10 flex items-center px-2">
          <div className='flex items-center gap-1'>
            <div className='w-4'>
              <IconRank rank={rank} />
            </div>
            <p className="text-xs font-medium text-white">{rank}</p>
          </div>
        </div>
        <div className="flex-1 flex items-center gap-2.5 px-4">
          <div className='w-6 h-6'>
            <ProfilePicture src={x.picture ?? undefined} twitterHandle={x.twitterHandle} />
          </div>
          
          {isMine ? (
            <div className='flex flex-col'>
              <p className="text-xs font-medium text-left text-white">
                {x.name}
              </p>
              <span className='text-[8px] text-white/50'> (You)</span>
            </div>
          ) : (
            <p className="text-xs font-medium text-left text-white">
              {x.name}
            </p>
          )}
        </div>
        <div className="flex items-center pr-2">
          <div className="rounded" style={{ border: '1px solid #FFF085'}}>
            <div className="px-2 py-0.5 bg-[#F0B10080]">
              <p className="text-xs font-semibold text-white">
                {formatNumber(x.totalHoney ?? x.dailyHoney)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const IconRank = ({ rank }: { rank: number }) => {
  const imgRank = rank === 1 ? imgRank1 : rank === 2 ? imgRank2 : rank === 3 ? imgRank3 : imgRank4
  return (
    <img src={imgRank} className="w-full aspect-square" />
  )
}

const TabButton = ({ children, active, onClick }: { children: ReactNode, active: boolean, onClick: () => void }) => {
  const sound = useSound();
  if (active) {
    return (
      <button onClick={() => {
        sound.play('click');
        onClick();
      }}>
        <InnerShape cornerDiagonal={12} cornerRadius={6} strokeWidth={1}>
          <div className="px-4 py-2">
            <p className="text-sm text-white font-blinko">{children}</p>
          </div>
        </InnerShape>
      </button>
    )
  }

  return (
    <button className="px-4 py-2 rounded-xl" onClick={() => {
      sound.play('click');
      onClick();
    }}>
      <p className="text-sm text-white/80 font-blinko">{children}</p>
    </button>
  )
}
